import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./BlogPost.css";
import AOS from "aos";
import "aos/dist/aos.css";

function BlogPost() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    AOS.init();
    AOS.refresh();
    window.scrollTo(0, 0);

    const fetchBlog = async () => {
      if (!id) {
        setError("Invalid blog ID.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/blog/${id}`);
        if (!response.ok) throw new Error("Failed to fetch blog details");
        const data = await response.json();
        setBlog(data);
      } catch (err) {
        setError("Unable to load this blog post.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id, API_BASE_URL]);


  // ============================================================
  //   ADVANCED FORMATTER FOR PARAGRAPHS + LISTS (COLON SECTIONS)
  // ============================================================
  const formatContent = (text) => {
    if (!text) return [];

    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const output = [];
    let currentList = [];
    let isColonList = false;

    const pushList = () => {
      if (currentList.length > 0) {
        output.push({ type: "list", items: currentList });
        currentList = [];
      }
      isColonList = false;
    };

    lines.forEach((line) => {
      // CASE 1: Heading that ends with ":"
      if (line.endsWith(":")) {
        pushList();
        output.push({ type: "heading", text: line.replace(/:$/, "") });
        isColonList = true;
        return;
      }

      // CASE 2: Numbered list "1. Something"
      if (/^\d+\.\s+/.test(line)) {
        currentList.push(line.replace(/^\d+\.\s+/, ""));
        return;
      }

      // CASE 3: Bullet-like descriptive lines e.g. "A working table – something"
      if (isColonList && /[-–]/.test(line)) {
        currentList.push(line);
        return;
      }

      // NORMAL PARAGRAPH
      pushList();
      output.push({ type: "paragraph", text: line });
    });

    // Final cleanup
    pushList();
    return output;
  };


  if (loading) {
    return <p className="blogPost-loadingText">Loading blog...</p>;
  }

  if (error) {
    return (
      <div className="blogPost-page">
        <p className="blogPost-errorText">{error}</p>
        <button className="blogPost-button" onClick={() => navigate(-1)}>
          Back to Blogs
        </button>
      </div>
    );
  }

  if (!blog) return null;

  const formatted = formatContent(blog.content);

  return (
    <div className="blogPost-page">
      <div className="banner-blog">
        <div className="banner-sect">
          <p className="banner-title">BLOG POST</p>
        </div>
      </div>

      <div className="blogPost-container">
        <div className="blogPost-content" data-aos="fade-up">
          <h1 className="blogPost-title">{blog.title}</h1>

          <p className="blogPost-meta">
            By {blog.author || "Unknown"} •{" "}
            {blog.publishedAt
              ? new Date(blog.publishedAt).toLocaleDateString()
              : "Unknown date"}
          </p>

          {blog.imagePath && (
            <div className="blogPost-imageWrapper">
              <img
                src={`${API_BASE_URL}${blog.imagePath}`}
                alt={blog.title}
              />
            </div>
          )}

          <div className="blogPost-textArea">
            {formatted.map((item, index) => {
              if (item.type === "heading")
                return (
                  <h3 className="blogPost-sectionTitle" key={index}>
                    {item.text}
                  </h3>
                );

              if (item.type === "list")
                return (
                  <ul className="blogPost-list" key={index}>
                    {item.items.map((li, i) => (
                      <li key={i}>{li}</li>
                    ))}
                  </ul>
                );

              return (
                <p key={index} className="blogPost-text">
                  {item.text}
                </p>
              );
            })}
          </div>

          <div className="blogPost-action">
            <button className="blogPost-button" onClick={() => navigate(-1)}>
              Back to Blogs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
